// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AssetFilter, AssetSortProperty } from '@/asset/api/get-assets';
import { AssetTable } from '@/asset/components/asset-table';
import { AssetTableSkeleton } from '@/asset/components/asset-table-skeleton';
import { AssetSearchListing } from '@/asset/hooks/use-asset-filters';
import { AssetTableColumn } from '@/constants/asset-table-column';
import { SortDirection } from '@/constants/sort';
import { useSort } from '@/hooks/use-sort';
import { Typography } from '@mui/material';
import { Suspense } from 'react';

const ASSET_TABLE_COLUMNS: AssetTableColumn[] = [
  'serial',
  'location',
  'owner',
  'activity',
];

interface Props {
  /**
   * The ID of the item for which to display assets.
   */
  itemId: string;
  handlePageChange?: (page: number) => void;
  assetSearchParams?: AssetSearchListing;
}

/**
 * A table of assets for a single item. This table displays the serial,
 * location, owner, and recent activity of each asset.
 */
export function ItemAssetTable(props: Props) {
  const { sort, setSort } = useSort<AssetSortProperty>('serial');
  const DEFAULT_SORT_PROPERTY: AssetSortProperty = 'serial';
  const DEFAULT_SORT_DIRECTION: SortDirection = 'asc';

  const filter: AssetFilter = {
    pageSize: 50,
    pageNumber: props.assetSearchParams?.page ?? 1,
    sort: {
      property:
        props.assetSearchParams?.sort ?? sort.property ?? DEFAULT_SORT_PROPERTY,
      direction:
        props.assetSearchParams?.direction ??
        sort.direction ??
        DEFAULT_SORT_DIRECTION,
    },
    productIds: [props.itemId],
  };

  return (
    <Suspense
      fallback={<AssetTableSkeleton columns={ASSET_TABLE_COLUMNS.length} />}
    >
      <AssetTable
        sort={sort}
        filter={filter}
        columns={ASSET_TABLE_COLUMNS}
        onSort={setSort}
        onPageChange={props.handlePageChange}
        emptyState={
          <Typography variant="body2" color="text.secondary">
            There are no assets for this item
          </Typography>
        }
      />
    </Suspense>
  );
}
